import { Stack, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { TableRow } from './_components/TableRow'
import PlusSvg from '~/@ui/_sharedSvgs/ui/Plus.svg?react'
import EurSvg from '~/@ui/_sharedSvgs/ui/Eur.svg?react'
import CoinsSvg from '~/@ui/_sharedSvgs/ui/Coins.svg?react'
import StarSvg from '~/@ui/_sharedSvgs/ui/Star.svg?react'
import {
  IInvestmentOrder,
  IInvestorProfileInvestmentOverview,
  IUser,
} from '~/@api/generated-api-types/global.types'
import { SummaryChildrenValues } from '~/@biz_investor/portfolio/OverviewTab/GraphSummaryChildren'
import { InfoBubble } from '~/@ui/info-bubble/InfoBubble'
import { GraphSummary } from '~/@biz_investor/opportunity-detail/GraphSummary'
import { SummaryPensionValues } from '~/@biz_investor/opportunity-detail/SummaryPensionValues'
import { useMemo } from 'react'
import { calculateInvestmentProjections } from '~/@ui/chart/_utils/investmentCalculator'
import Chart from '~/@ui/chart/Chart'

type Props = {
  user: IUser | null
  investmentOverview: IInvestorProfileInvestmentOverview | null
  firstInvestment: IInvestmentOrder | null
}

export const OverviewTab = ({
  user,
  investmentOverview,
  firstInvestment,
}: Props) => {
  const isEmpty = !(
    user?._investorProfile?._investmentOrdersCount ||
    investmentOverview?.accountValue ||
    investmentOverview?.funds ||
    investmentOverview?.reservedAmount
  )

  const isSavingsChildren =
    firstInvestment?.productSnapshot.slug === 'buducnost-mojich-deti'
  const isSavingsRetirement =
    firstInvestment?.productSnapshot.slug ===
    'zabezpecenie-financii-na-dochodok'

  const oneTimeInvestmentAmount = firstInvestment?.hasOneTimeInvestment
    ? firstInvestment.oneTimeInvestmentAmount || 0
    : 0
  const monthlyInvestmentAmount = firstInvestment?.monthlyInvestmentAmount || 0
  const interestPerAnnum =
    firstInvestment?.productSnapshot.interestPerAnnum || 0
  const initialDeposit = oneTimeInvestmentAmount + monthlyInvestmentAmount

  const investmentYears = 33

  const series = useMemo(
    () =>
      calculateInvestmentProjections({
        monthlyContribution: monthlyInvestmentAmount,
        investmentYears,
        initialDeposit,
        rates: {
          earlyWithdraw: 0.0359,
          bonaship: 0.0718,
          cash: 0,
          bankSavings: 0.015,
          inflation: 0.04,
        },
      }),
    [monthlyInvestmentAmount, investmentYears, initialDeposit]
  )

  return (
    <Stack className="px-6 pt-8">
      <Typography className="text-base font-bold text-zBlack200">
        Prehľad účtu
      </Typography>

      <Stack className="gap-2.5 p-4 mt-4 bg-zBlack200 rounded-4d">
        <Stack className="flex-row items-center gap-2">
          <Typography className="text-base font-bold text-zWhite200">
            Moja peňaženka
          </Typography>
        </Stack>
        <Stack className="flex-row justify-between items-center">
          <Typography className="text-4.5d font-bold text-zWhite200 tabular-nums">
            {`${(investmentOverview?.funds || 0).toLocaleString('sk-SK')} €`}
          </Typography>
          <Link
            to="/investor/add-funds"
            className="flex flex-row items-center gap-2 no-underline">
            <Typography className="text-sm font-semibold text-zWhite200">
              Pridať prostriedky
            </Typography>
            <PlusSvg />
          </Link>
        </Stack>
      </Stack>

      <Stack className="divide-y divide-zGrey250">
        {!isEmpty && (
          <TableRow
            label="Rezervované prostriedky"
            value={investmentOverview?.reservedAmount || 0}
            svg={EurSvg}
            hintText="Rezervované prostriedky"
          />
        )}
        <TableRow
          label="Investované"
          value={investmentOverview?.investedAmount || 0}
          svg={CoinsSvg}
          hintText="Investované"
        />
        <TableRow
          label="Hodnota účtu"
          value={investmentOverview?.accountValue || 0}
          svg={StarSvg}
          hintText="Hodnota účtu"
        />
      </Stack>

      {firstInvestment && (
        <>
          <Stack className="pt-8">
            <Typography className="text-base font-bold text-zBlack200">
              {firstInvestment.productSnapshot.name}
            </Typography>
          </Stack>
          <Stack className={`p-4 bg-zGrey200 rounded-4d mt-3`}>
            <Stack className="flex-row gap-1 mb-4">
              <InfoBubble text="Graf ukazuje, ako sa vaša investícia vyvíja v čase." />
              <Typography className="text-base font-bold text-zBlack200">
                Graf výnosov
              </Typography>
            </Stack>

            <Stack className="relative border border-zGrey700 rounded-t-2d">
              <Chart
                theme="light"
                slug={firstInvestment.productSnapshot.slug}
                series={series}
                months={investmentYears * 12}
              />

              <GraphSummary
                className="z-10"
                bonaship={series.bonashipValue[360] || 0}
                cash={series.cashValue[360] || 0}
                months={360}
                intPerAnn={series.rates.bonaship}
                earlyWithdraw={0}
                inflation={0}
                bankSavings={series.bankSavingsValue[360] || 0}
                slug={firstInvestment.productSnapshot.slug}
              />
            </Stack>

            <Stack className="bottom-[0px] w-full flex-row justify-between mt-2.5">
              <Typography className="text-xs font-medium text-zGrey400">
                0 rokov
              </Typography>
              <Typography className="text-xs font-medium text-zGrey400">
                {isSavingsRetirement ? 20 : 15} rokov
              </Typography>
              <Typography className="text-xs font-medium text-zGrey400">
                {isSavingsRetirement ? 40 : 30} rokov
              </Typography>
            </Stack>
          </Stack>

          {isSavingsChildren && (
            <SummaryChildrenValues
              className="mt-4"
              years18={Math.round(
                series.bonashipValue[216] || 0
              ).toLocaleString('sk-SK')}
              years25={Math.round(
                series.bonashipValue[300] || 0
              ).toLocaleString('sk-SK')}
              years30={Math.round(
                series.bonashipValue[360] || 0
              ).toLocaleString('sk-SK')}
              interestPerAnnum={interestPerAnnum}
            />
          )}

          {isSavingsRetirement && (
            <SummaryPensionValues
              className="mt-3"
              theme="light"
              deposit={series.cashValue[360]}
              bonaship={series.bonashipValue[360] || 0}
              earlyWithdraw={series.earlyWithdrawValue[360] || 0}
            />
          )}
        </>
      )}
    </Stack>
  )
}
